import React, { useState, useEffect } from "react";
import { Dialog } from '../dialogs/dialog';
import { Button } from "primereact/button";
import { ColorPicker } from "primereact/colorpicker";
import { Slider } from "primereact/slider";
import { useThemesData } from "../../providers/theme-management-provider";
import { ColorIntf, FontResource, getFontFamilyName, parseColor } from "../../general/utilities";
import { Dropdown } from "primereact/dropdown";
import { Divider } from "primereact/divider";     // Added for visual separation
import { Chips } from "primereact/chips";

interface TypographyItem {
    color: ColorIntf;
    size: number;
    fontFamily: string;
}

interface TypographyConfig {
    h1: TypographyItem;
    h2: TypographyItem;
    h3: TypographyItem;
    h4: TypographyItem;
    h5: TypographyItem;
    h6: TypographyItem;
}

export const TypographyDialog: React.FC<{ visible: boolean; onHide: () => void }> = ({ visible, onHide }) => {
    const { findCssValue, bulkUpdateCssProperties, cssSelectors } = useThemesData();

    // --- NEW GLOBAL STATE ---
    const [globalFontStack, setGlobalFontStack] = useState<string[]>([]);
    const [baseSize, setBaseSize] = useState<number>(16);
    const [selectedFont, setSelectedFont] = useState<string>("Default");

    const defColor: ColorIntf = parseColor(findCssValue(":root", "--text-color") || "#333333");

    const [config, setConfig] = useState<TypographyConfig>({
        h1: { color: defColor, size: 2.5, fontFamily: 'Default' },
        h2: { color: defColor, size: 2.0, fontFamily: 'Default' },
        h3: { color: defColor, size: 1.75, fontFamily: 'Default' },
        h4: { color: defColor, size: 1.5, fontFamily: 'Default' },
        h5: { color: defColor, size: 1.25, fontFamily: 'Default' },
        h6: { color: defColor, size: 1.0, fontFamily: 'Default' },
    });

    useEffect(() => {
        if (visible) {
            const serialize = (c: ColorIntf): string => `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;
            const defColor: ColorIntf = parseColor(findCssValue(":root", "--text-color") || "#333333");
            const defColorString: string = serialize(defColor);

            const parseValue = (val: string | null | undefined): number => {
                if (!val) return 1;
                const numericPart = val.replace(/[^0-9.]/g, ''); 
                return parseFloat(numericPart) || 1;
            };

            const rawRootFont = findCssValue(":root", "--font-family") || findCssValue(":root", "font-family") || "";
            const stack = rawRootFont.split(',').map(f => f.replace(/['"]/g, "").trim()).filter(Boolean);
            setGlobalFontStack(stack.length > 0 ? stack : ["Inter var", "sans-serif"]);

            const rootSize = parseInt(findCssValue(":root", "font-size")?.replace('px', '') || "16");
            setBaseSize(rootSize);

            const getTagStyle = (tag: string) => ({
                color: parseColor(findCssValue(tag, "color") || defColorString),
                size: parseValue(findCssValue(tag, "font-size") || config[tag as keyof TypographyConfig].size.toFixed(2).toString()),
                fontFamily: findCssValue(tag, "font-family") || "Default"
            });

            setConfig({
                h1: getTagStyle("h1"),
                h2: getTagStyle("h2"),
                h3: getTagStyle("h3"),
                h4: getTagStyle("h4"),
                h5: getTagStyle("h5"),
                h6: getTagStyle("h6"),
            });
        }
    }, [visible, findCssValue]);

    const handleApply = (): void => {
        const serialize = (c: ColorIntf) => `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;

        // 1. UPDATE GLOBAL ROOT
        const rootProps = [
            { property: "font-size", value: `${baseSize}px` }
        ];
        const fontValue = globalFontStack.map(f => `"${f}"`).join(', ');
        bulkUpdateCssProperties(":root", [
            { property: "--font-family", value: fontValue },
            { property: "font-family", value: fontValue },
            { property: "font-size", value: `${baseSize}px` }
        ], 'merge');
        bulkUpdateCssProperties(":root", rootProps, 'merge');

        // 2. UPDATE HEADINGS (Your existing logic)
        const tags = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;
        tags.forEach((tag) => {
            const { color, size, fontFamily } = config[tag];
            const props = [
                { property: "color", value: serialize(color) },
                { property: "font-size", value: `${size.toFixed(2)}rem` }
            ];
            if (fontFamily !== "Default") {
                props.push({ property: "font-family", value: fontFamily });
            }
            bulkUpdateCssProperties(tag, props, 'merge');
        });

        onHide();
    };

    const addFontToStack = () => {
        if (selectedFont !== "Default" && !globalFontStack.includes(selectedFont)) {
            setGlobalFontStack([...globalFontStack, selectedFont]);
            setSelectedFont("Default");
        }
    };

    const internalFonts: string[] = cssSelectors
        .flatMap(c => c.children)
        .filter(n => n.selector.startsWith("__font-face-"))
        .map(n => n.properties.find(p => p.property === "font-family")?.value.replace(/['"]/g, "") || "")
        .filter((value, index, self) => value !== "" && self.indexOf(value) === index);

    const activeImports: FontResource[] = cssSelectors
        .flatMap(c => c.children)
        .filter(n => n.selector.startsWith("__import_"))
        .map(n => ({
            url: n.properties.find(p => p.property === "url")?.value || "",
            name: getFontFamilyName(n.properties.find(p => p.property === "url")?.value || "")
        }));

    const allAvailableFonts = Array.from(new Set([
        "Default",
        "sans-serif",
        "serif",
        "monospace",
        ...internalFonts,
        ...activeImports.map(i => i.name)
    ])).map(f => ({ name: f, value: f }));

    const fontOptions = [{ name: "Default", url: "" }, ...activeImports];

    return (
        <Dialog header="Typography settings" visible={visible} onHide={onHide} style={{ width: '700px' }}>
            <div className="p-fluid">
                <div className="flex gap-4 mb-2">
                    <div className="flex-grow-1">
                        <label className="font-bold block mb-2">Global font stack</label>
                        <Chips 
                            value={globalFontStack} 
                            onChange={(e) => setGlobalFontStack(e.value || [])}
                            separator=","
                            placeholder="Type font and hit Enter"
                            className="w-full"
                        />
                        <div className="flex flex-wrap gap-2 mt-2">
                            <Dropdown 
                                value={selectedFont}
                                options={allAvailableFonts}
                                optionLabel="name"
                                optionValue="value"
                                editable
                                onChange={(e) => setSelectedFont(e.value)} 
                                scrollHeight="500px"
                            />
                            <Button icon="pi pi-plus" onClick={addFontToStack} />
                        </div>
                    </div>
                </div>

                <Divider align="center" className="my-4">
                    <span className="text-500 font-normal text-xs uppercase">Headings scale</span>
                </Divider>

                {/* --- EXISTING HEADINGS SECTION --- */}
                {(["h1", "h2", "h3", "h4", "h5", "h6"] as const).map((tag) => (
                    <div key={tag} className="flex align-items-center mb-3 gap-3">
                        <label style={{ width: '40px', fontWeight: 'bold' }}>{tag.toUpperCase()}:</label>
                        <div style={{ width: '64px', flexShrink: 0 }}>
                            <ColorPicker 
                                value={`#${((1 << 24) + (config[tag].color.r << 16) + (config[tag].color.g << 8) + config[tag].color.b).toString(16).slice(1)}`} 
                                onChange={(e) => {
                                    const val = e.value as string;
                                    const hexVal = val.startsWith('#') ? val : `#${val}`;
                                    setConfig(prev => ({
                                        ...prev, 
                                        [tag]: {...prev[tag], color: parseColor(hexVal)}
                                    }));
                                }}
                            />
                        </div>
                        <Slider 
                            value={config[tag].size} min={1} max={5} step={0.1}
                            style={{ width: '150px' }} 
                            onChange={(e) => {
                                const newValue = parseFloat((e.value as number).toFixed(2));
                                setConfig(prev => ({...prev, [tag]: {...prev[tag], size: newValue}}))
                            }} 
                        />
                        <span style={{ width: '80px' }}>{config[tag].size}rem</span>

                        <Dropdown 
                            value={config[tag].fontFamily}
                            options={fontOptions}
                            optionLabel="name"
                            optionValue="name"
                            onChange={(e: any) => setConfig(prev => ({
                                ...prev,
                                [tag]: { ...prev[tag], fontFamily: e.value }
                            }))}
                            placeholder="Select font"
                            style={{ width: '150px' }}
                        />
                    </div>
                ))}
            </div>
            <div className="admin-toolbar flex justify-content-end mt-4 gap-2">
                <Button label="Cancel" onClick={onHide} className="p-button-secondary" />
                <Button label="Apply" onClick={handleApply} className="p-button-primary" />
            </div>
        </Dialog>
    );
}